import {ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, Signal} from "@angular/core";
import {ButtonComponent} from "../../buttons/button/button.component";
import {ButtonProminence, ButtonTier} from "../../buttons/button/button";
import {LinkComponent} from "../../buttons/link/link.component";
import {ContentMessageComponent} from "../../notification/message/content-message.component";
import {MessageLevel} from "../../notification/message/message";
import {OrderType} from '../../order/order';
import {OrderModel} from '../../order/order.model';
import {UnavailabilityReason} from '../../order/order-initiation.service';
import {OrderingContextModel} from "../ordering-context.model";
import {TranslateModule} from '@ngx-translate/core';
import {HtmlPipe} from '../../utils/html.pipe';
import {ConfigurationModel} from '../../configuration/configuration.model';
import {IdentityModel} from '../../identity/identity.model';
import {MenuModel} from '../../menu/menu.model';

@Component({
    selector: "woa-ordering-context-panel",
    templateUrl: "./ordering-context-panel.component.html",
    styleUrl: "./ordering-context-panel.component.scss",
    imports: [
        ButtonComponent,
        ContentMessageComponent,
        HtmlPipe,
        LinkComponent,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderingContextPanelComponent {
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public orderModel: OrderModel = inject(OrderModel);
    public identityModel: IdentityModel = inject(IdentityModel);
    public menuModel: MenuModel = inject(MenuModel);
    private configurationModel: ConfigurationModel = inject(ConfigurationModel);

    public readonly timeOnly: InputSignal<boolean> = input<boolean>(false);

    ButtonTier = ButtonTier;
    ButtonProminence = ButtonProminence;
    MessageLevel = MessageLevel;

    public readonly summaryTable: Signal<string> = computed(() => this.orderingContextModel.currentTableNumberLabel());

    public readonly showCurrentStore: Signal<boolean> = computed<boolean>(() =>
        this.orderingContextModel.currentStore() &&
        (this.configurationModel.isWaiterActor() ||
            (this.orderingContextModel.orderType() === OrderType.ORDER_AHEAD && !this.orderingContextModel.singleStoreLocation()) ||
            (this.orderingContextModel.orderType() === OrderType.ORDER_AT_TABLE && this.orderingContextModel.canChangeTableStore())));

    public readonly showAddress: Signal<boolean> = computed<boolean>(() =>
        this.orderingContextModel.orderType() === OrderType.ORDER_DELIVERY && this.orderingContextModel.addressTrusted());

    public readonly fromStore: Signal<string> = computed(() =>
        this.orderingContextModel.orderType() === OrderType.ORDER_AT_TABLE ? "woa.heading.ordering-context.atStore" : "woa.heading.ordering-context.fromStore");

    public readonly showRsaAction: Signal<boolean> = computed(() =>
        this.configurationModel.isWaiterActor() && this.orderingContextModel.isConfirmedTableOrdering());

    public readonly showContextAction: Signal<boolean> = computed<boolean>(() =>
        this.orderingContextModel.canSelectOrderTime() ||
        (!this.timeOnly() &&
            (this.orderingContextModel.canChangeStore() ||
                !this.orderingContextModel.currentStore() ||
                !this.orderingContextModel.singleOrderType() ||
                this.orderingContextModel.orderType() !== OrderType.ORDER_AHEAD)));

    public readonly showWaiterName: Signal<boolean> = computed<boolean>(() =>
        this.configurationModel.requireWaiterName() && !!this.identityModel.waiterName());

    public readonly query: Signal<string> = computed(() => {
        switch (this.orderingContextModel.orderType()) {
            case OrderType.ORDER_AHEAD:
                return !this.orderingContextModel.currentStore() && !this.timeOnly() ? "woa.heading.ordering-context.selectStore.query"
                    : !this.orderingContextModel.expectedCompletion() && this.orderingContextModel.canSelectOrderTime() && !this.orderingContextModel.canShowAsapSelectionTime() ? "woa.heading.ordering-context.selectTime.query"
                        : undefined;
            case OrderType.ORDER_DELIVERY:
                return !this.orderingContextModel.addressTrusted() && !this.timeOnly() ? "woa.heading.ordering-context.enterAddress.query"
                    : !this.orderingContextModel.currentStore() && !this.timeOnly() ? "woa.heading.ordering-context.selectStore.query"
                        : !this.orderingContextModel.expectedCompletion() && this.orderingContextModel.canSelectOrderTime() && !this.orderingContextModel.canShowAsapSelectionTime() ? "woa.heading.ordering-context.selectTime.query"
                            : undefined;
            case OrderType.ORDER_AT_TABLE:
                return !this.summaryTable() && (this.orderingContextModel.currentStore() || !this.orderingContextModel.hasStoreGroup()) ? (this.configurationModel.isWaiterActor() ? "woa.heading.ordering-context.selectTable.waiter" : "woa.heading.ordering-context.selectTable.query")
                    : !this.orderingContextModel.currentStore() ? "woa.heading.ordering-context.selectStore.query"
                        : undefined;
            default:
                return "woa.heading.ordering-context.orderType.query";
        }
    });

    public readonly primaryStore: Signal<string> = computed(() =>
        this.showCurrentStore() && this.query() ? this.orderingContextModel.currentStore().collectiveStoreName : undefined);

    public readonly primaryAddress: Signal<string> = computed(() =>
        this.showAddress() && this.query() ? this.orderingContextModel.address().street || this.orderingContextModel.address().city : undefined);

    public readonly secondaryStore: Signal<string> = computed(() =>
        this.showCurrentStore() && !this.query() ? this.orderingContextModel.currentStore().collectiveStoreName : undefined);

    public readonly secondaryAddress: Signal<string> = computed(() =>
        this.showAddress() && !this.query() ? this.orderingContextModel.address().street || this.orderingContextModel.address().city : undefined);

    public readonly storeDetailsLink: Signal<string> = computed(() =>
        this.configurationModel.isConsumerActor() && this.orderingContextModel.orderType() === OrderType.ORDER_AHEAD && this.orderingContextModel.currentStore() && !this.query() ? "woa.button.ordering-context.viewCurrentStore" : undefined);

    public readonly contextActionLabel: Signal<string> = computed(() =>
        this.orderingContextModel.orderType() == OrderType.ORDER_DELIVERY && !this.orderingContextModel.addressTrusted() ? "woa.button.ordering-context.enter"
            : this.query() ? "woa.button.ordering-context.select"
                : "woa.button.ordering-context.change");

    public readonly summaryOrderType: Signal<string> = computed(() => {
        switch (this.orderingContextModel.orderType()) {
            case OrderType.ORDER_AHEAD:
                return this.orderingContextModel.currentStore() ? "woa.common.order.ahead.placement.verb" : undefined;
            case OrderType.ORDER_DELIVERY:
                return this.orderingContextModel.addressTrusted() ? "woa.common.order.delivery.placement.verb" : undefined;
            case OrderType.ORDER_AT_TABLE:
                return this.summaryTable() || this.primaryStore() ? "woa.common.order.atTable.placement.verb" : undefined;
            default:
                return undefined;
        }
    });

    public readonly summaryExpectedTime: Signal<string> = computed(() =>
        this.orderingContextModel.expectedCompletion() &&
        (this.orderingContextModel.orderType() === OrderType.ORDER_AHEAD ||
            (this.orderingContextModel.orderType() === OrderType.ORDER_DELIVERY && !this.primaryAddress())) ? this.orderingContextModel.formattedExpectedCompletion() : undefined);

    public readonly summaryEarliestTime: Signal<string> = computed(() =>
        !this.summaryExpectedTime() &&
        (this.orderingContextModel.orderType() === OrderType.ORDER_AHEAD || this.orderingContextModel.orderType() === OrderType.ORDER_DELIVERY) &&
        this.orderModel.initiatedOrder().earliestLocalTime &&
        !this.orderModel.showOrderTimeAllocationNotice() ? this.orderModel.formattedEarliestTime() : undefined);

    public readonly showAsap: Signal<boolean> = computed<boolean>(() =>
        (this.orderingContextModel.orderType() === OrderType.ORDER_AHEAD || this.orderingContextModel.orderType() === OrderType.ORDER_DELIVERY) &&
        this.summaryOrderType() && !this.orderingContextModel.expectedCompletion() && this.orderingContextModel.canShowAsapSelectionTime());

    public readonly showSelectTimeLink: Signal<boolean> = computed<boolean>(() =>
        this.orderingContextModel.canSelectOrderTime() && this.orderModel.initiatedOrder().unavailabilityReason !== UnavailabilityReason.UNAVAILABLE_OFFLINE);
}
